import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UsersThree, X, CheckCircle, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const DISMISS_KEY = "bt_invite_nudge_dismissed";

export default function InviteTeammateNudge() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isManager = ["owner", "admin"].includes(user?.role);
  const [show, setShow] = useState(false);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [seatLimit, setSeatLimit] = useState(false);

  useEffect(() => {
    if (!isManager) return;
    if (localStorage.getItem(DISMISS_KEY) === "1") return;
    Promise.all([
      api.get("/team/members").then((r) => r.data).catch(() => []),
      api.get("/team/invites").then((r) => r.data).catch(() => []),
    ]).then(([members, invites]) => {
      // Solo team with no pending invites → nudge to collaborate
      if ((members?.length || 0) + (invites?.length || 0) <= 1) setShow(true);
    });
  }, [isManager]);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setShow(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/team/invite", { email });
      if (data.email_sent) toast.success(`Invitation emailed to ${email}`);
      else {
        navigator.clipboard?.writeText(data.invite_url).catch(() => {});
        toast.success("Invite link copied to clipboard");
      }
      setSent(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403 && detail && typeof detail === "object" && detail.code === "seat_limit") {
        setSeatLimit(true);
      } else {
        toast.error(formatApiErrorDetail(detail));
      }
    } finally {
      setSending(false);
    }
  };

  if (!isManager || !show) return null;

  return (
    <div
      data-testid="invite-teammate-nudge"
      className="relative border border-border bg-secondary/40 mb-8 reveal overflow-hidden"
    >
      <div className="absolute inset-y-0 left-0 w-1 bg-emerald-600" />
      <button
        onClick={dismiss}
        data-testid="invite-nudge-dismiss"
        aria-label="Dismiss"
        className="absolute top-3 right-3 text-muted-foreground hover:text-primary transition-colors duration-200"
      >
        <X size={16} weight="bold" />
      </button>

      <div className="p-6 pl-7">
        {sent ? (
          <div className="flex items-center gap-3" data-testid="invite-nudge-success">
            <CheckCircle size={22} weight="fill" className="text-emerald-600 shrink-0" />
            <div>
              <p className="font-heading font-bold">Invitation on its way 🎉</p>
              <p className="text-sm text-muted-foreground mt-0.5">
                Manage teammates and pending invites anytime on the{" "}
                <button onClick={() => navigate("/team")} className="text-primary underline" data-testid="invite-nudge-goto-team">
                  Team page
                </button>.
              </p>
            </div>
          </div>
        ) : seatLimit ? (
          <div data-testid="invite-nudge-seatlimit">
            <p className="font-heading font-bold">You've reached your seat limit</p>
            <p className="text-sm text-muted-foreground mt-1 mb-4">
              Upgrade your plan to bring more teammates into BuildTrack.
            </p>
            <Button onClick={() => navigate("/pricing")} className="rounded-none" data-testid="invite-nudge-upgrade">
              View plans <ArrowRight size={16} className="ml-1" />
            </Button>
          </div>
        ) : (
          <div className="flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
            <div className="flex items-start gap-3 md:max-w-sm">
              <UsersThree size={24} weight="bold" className="text-emerald-700 shrink-0 mt-0.5" />
              <div>
                <p className="font-heading font-bold">Build faster, together</p>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Invite a teammate to share projects, vendors, and quotations. Collaboration keeps everyone on budget.
                </p>
              </div>
            </div>
            <form onSubmit={submit} className="flex flex-1 gap-2 md:justify-end" data-testid="invite-nudge-form">
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="teammate@company.com"
                data-testid="invite-nudge-email-input"
                className="rounded-none md:max-w-xs"
              />
              <Button type="submit" disabled={sending} className="rounded-none shrink-0" data-testid="invite-nudge-send-button">
                {sending ? "Sending…" : "Send invite"}
              </Button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
